import React, { useCallback, useContext, useState } from "react";

import {
  Alert,
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { AdminQuotaUsageSeed_WindowStart } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import { RestrictionName } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { fmtUTCDate } from "./credit-periods";
import { getOrgUserList } from "api/endpoints/organisation";
import { seedUserRestrictionUsage } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import useDebouncedEffect from "helpers/hooks/use-debounced-effect";
import { toUUID } from "helpers/s12-helpers";

const DEFAULT_CREDITS = 500;
const DEFAULT_EVENT_COUNT = 100;
const DEFAULT_FEATURE = "seed";
const DEFAULT_BILLABLE = true;
const MAX_FEATURE_LENGTH = 100;
const MIN_CREDITS = 1;
const MAX_CREDITS = 1000000;
const MIN_EVENTS = 1;
const MAX_EVENTS = 5000;

// Cap the option list; the picker searches server-side so users beyond the
// first page stay reachable by name/email.
const PICKER_PAGE_SIZE = 100;

// The window always ends at now; these choose where it starts. The server
// resolves the concrete time (it alone knows the period start and grant times).
const WINDOW_OPTIONS: Array<{
  value: AdminQuotaUsageSeed_WindowStart;
  label: string;
}> = [
  {
    value: AdminQuotaUsageSeed_WindowStart.CURRENT_PERIOD,
    label: "Current period start → now",
  },
  {
    value: AdminQuotaUsageSeed_WindowStart.LATEST_PURCHASED_GRANT,
    label: "Latest purchased pack → now",
  },
];

// Suggestions only — any tag is accepted. The authoritative registry lives in
// api-ai-agents, so a closed list here would drift as tags are added.
const FEATURE_SUGGESTIONS = ["seed", "custom-agent", "sc-assistant-agent"];

const userOptionLabel = (user: User): string => {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const name = fullName || user.id;
  return user.email ? `${name} (${user.email})` : name;
};

interface SeedUsageModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roleId: string;
  regionCode: string;
}

interface SeedResult {
  eventsIngested: number;
  from?: Date;
  to?: Date;
}

// SeedUsageModal seeds synthetic AI credit usage for a single user so the
// burndown chart can be exercised without real traffic. It is a non-production
// testing aid; api-restrictions refuses to seed in production, classifies each
// event free vs overage itself, and resolves the concrete window from the
// chosen start (always ending now, since OpenMeter ignores future-dated usage).
// The operator picks a user, a window start, the total credits and event count.
const SeedUsageModal = ({
  open,
  onClose,
  onSuccess,
  roleId,
  regionCode,
}: SeedUsageModalProps) => {
  const { showError } = useContext(SnackbarContext);

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [credits, setCredits] = useState(DEFAULT_CREDITS);
  const [eventCount, setEventCount] = useState(DEFAULT_EVENT_COUNT);
  const [windowStart, setWindowStart] =
    useState<AdminQuotaUsageSeed_WindowStart>(
      AdminQuotaUsageSeed_WindowStart.CURRENT_PERIOD,
    );
  const [feature, setFeature] = useState(DEFAULT_FEATURE);
  const [billable, setBillable] = useState(DEFAULT_BILLABLE);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [result, setResult] = useState<SeedResult | null>(null);

  const loadUsers = useCallback(
    async (query: string) => {
      try {
        const response = await getOrgUserList(
          {
            organizationId: roleId,
            pageSize: PICKER_PAGE_SIZE,
            offset: 0,
            sortDirection: SortDirection.ASC,
            sortType: UserSortType.FIRST_NAME,
            userStatus: UserStatus.UNSPECIFIED,
            query,
          },
          callOptionsHeaders(regionCode, roleId),
        );
        setUsers(response.users);
      } catch (err) {
        showError(err as GrpcError);
      }
    },
    [roleId, regionCode, showError],
  );

  // Load an initial page when the modal opens, then refetch (debounced) as the
  // operator types so users past the first page remain findable.
  useDebouncedEffect(
    () => {
      if (!open) return;
      loadUsers(userQuery);
    },
    300,
    [open, userQuery, loadUsers],
  );

  const isCreditsInvalid = credits < MIN_CREDITS || credits > MAX_CREDITS;
  const isEventsInvalid = eventCount < MIN_EVENTS || eventCount > MAX_EVENTS;
  const isFormComplete =
    !isCreditsInvalid && !isEventsInvalid && !!selectedUser;
  const isConfirmDisabled = !isFormComplete || !isConfirmed || isSeeding;

  const reset = () => {
    setSelectedUser(null);
    setUserQuery("");
    setCredits(DEFAULT_CREDITS);
    setEventCount(DEFAULT_EVENT_COUNT);
    setWindowStart(AdminQuotaUsageSeed_WindowStart.CURRENT_PERIOD);
    setFeature(DEFAULT_FEATURE);
    setBillable(DEFAULT_BILLABLE);
    setIsConfirmed(false);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedUser) return;
    setIsSeeding(true);
    setResult(null);
    try {
      const resp = await seedUserRestrictionUsage(
        {
          userId: toUUID(selectedUser.id),
          restrictionName: RestrictionName.AI_CREDIT_USAGE,
          totalUsage: credits,
          eventCount,
          windowStart,
          feature,
          billable,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      setResult({
        eventsIngested: resp.eventsIngested,
        from: resp.fromTime?.toDate(),
        to: resp.toTime?.toDate(),
      });
      // Refresh the underlying tab; keep the dialog open so the operator can see
      // the resolved window and re-seed if they want.
      onSuccess();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <ConfirmDialog
      title="Seed credit usage"
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      cancelBtnLabel="Close"
      confirmBtnLabel="Seed usage"
      disabled={isConfirmDisabled}
      disableCloseOnConfirm
      disableBackgroundClick={isSeeding}
    >
      <Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
        <Alert severity="warning">
          This ingests synthetic usage for the chosen user into OpenMeter, up to
          now. It is a non-production testing aid and draws down real
          entitlements; usage is classified free vs overage server-side. Seed
          from the latest purchased pack to see grant-backed (Purchased) usage
          rather than overage.
        </Alert>

        <FormControl size="small" fullWidth>
          <InputLabel id="seed-window-label">Seed window</InputLabel>
          <Select
            labelId="seed-window-label"
            label="Seed window"
            value={windowStart}
            onChange={(e) => setWindowStart(Number(e.target.value))}
            data-testid="seed-window-select"
          >
            {WINDOW_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          options={users}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          onInputChange={(_, value, reason) => {
            // Ignore the reset MUI fires when an option is picked; only the
            // operator's typing should drive the server-side search.
            if (reason === "input") setUserQuery(value);
          }}
          getOptionLabel={userOptionLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(options) => options}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Choose a user"
              placeholder="Search by name or email"
              inputProps={{
                ...params.inputProps,
                "data-testid": "seed-user-picker",
              }}
            />
          )}
        />

        <TextField
          label="Total credits"
          type="number"
          size="small"
          fullWidth
          value={credits}
          onChange={(e) => setCredits(Number(e.target.value))}
          inputProps={{
            min: MIN_CREDITS,
            max: MAX_CREDITS,
            "data-testid": "seed-credits-input",
          }}
          error={isCreditsInvalid}
          helperText={
            isCreditsInvalid
              ? `Value must be between ${MIN_CREDITS} and ${MAX_CREDITS.toLocaleString()}`
              : "Total credits distributed across the window"
          }
        />

        <TextField
          label="Number of events"
          type="number"
          size="small"
          fullWidth
          value={eventCount}
          onChange={(e) => setEventCount(Number(e.target.value))}
          inputProps={{
            min: MIN_EVENTS,
            max: MAX_EVENTS,
            "data-testid": "seed-event-count-input",
          }}
          error={isEventsInvalid}
          helperText={
            isEventsInvalid
              ? `Value must be between ${MIN_EVENTS} and ${MAX_EVENTS.toLocaleString()}`
              : "Synthetic events spread across the window with varied amounts"
          }
        />

        <Autocomplete
          freeSolo
          size="small"
          options={FEATURE_SUGGESTIONS}
          value={feature}
          onInputChange={(_, value) => setFeature(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Feature tag"
              helperText="Stamped on every seeded event. Leave as seed unless reproducing a product segment."
              inputProps={{
                ...params.inputProps,
                maxLength: MAX_FEATURE_LENGTH,
                "data-testid": "seed-feature-input",
              }}
            />
          )}
        />

        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                data-testid="seed-billable-checkbox"
                size="small"
              />
            }
            label="Billable"
          />
          <FormHelperText>
            Non-billable usage is excluded from the credit meter, so it does not
            draw the balance down.
          </FormHelperText>
        </FormControl>

        {result && (
          <Alert severity="success" data-testid="seed-result">
            Seeded {result.eventsIngested.toLocaleString()} events
            {result.from && result.to
              ? ` over ${fmtUTCDate(result.from)} – ${fmtUTCDate(result.to)}`
              : ""}
            .
          </Alert>
        )}

        <Alert
          severity="info"
          icon={false}
          sx={{ "& .MuiAlert-message": { width: "100%", py: 0 } }}
        >
          <FormControlLabel
            sx={{ m: 0, alignItems: "center" }}
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                data-testid="seed-confirmation-checkbox"
                size="small"
                sx={{ py: 0.5, mr: 0.5 }}
              />
            }
            label="I understand this writes usage into this environment's meters."
          />
        </Alert>
      </Stack>
    </ConfirmDialog>
  );
};

export default SeedUsageModal;
